import { create } from 'zustand';
import { rackCellCount, type RackKind } from '@eombor/contracts';
import type { LayoutDto } from '../../warehouse/api/warehouse.api.js';
import {
  placeInZone,
  rectWithinFloor,
  snap,
  snapRackPos,
  withinFloor,
  type PlanRect,
  type RackPlacement,
  type WarehouseFloor,
} from './plan-geometry.js';

/**
 * Konstruktorning **qoralama** holati.
 *
 * Hech narsa serverga darhol yuborilmaydi: skladchi rejani chizadi, ko'radi,
 * bekor qiladi va faqat "Saqlash" bosilganda o'zgarish ketadi. Sababi oddiy —
 * stellajni kichraytirish kataklarni O'CHIRADI, o'chirilgan katakda esa
 * mahsulot bo'lishi mumkin. Bunday amal tasodifiy sichqoncha harakati bilan
 * bo'lmasligi kerak.
 */

export interface DraftZone {
  /** Yangi zonada `local:<n>` — server identifikatori hali yo'q. */
  id: string;
  code: string;
  name: string;
  color: string;
  plan: PlanRect;
}

export interface DraftRack {
  id: string;
  zoneId: string;
  code: string;
  name: string | null;
  /** `FLOOR` — ochiq maydon: bir qavat, temirsiz. Yaratilgach o'zgarmaydi. */
  kind: RackKind;
  pos: { x: number; y: number; rotationDeg: number };
  /** Ichki kenglik — ramaning chuqurligi. */
  depthM: number;
  /** Proyomlar kengligi, chapdan o'ngga. Umumiy uzunlik — yig'indisi. */
  /** HAR QATORNING proyomlari, pastdan tepaga. */
  bays: number[][];
  /** Qatorlar balandligi, pastdan tepaga. Umumiy balandlik — yig'indisi. */
  levelHeights: number[];
  /** Serverdagi joriy katak soni — kamaytirishda ogohlantirish uchun. */
  serverCells: number;
  /**
   * Shu javonning MAHSULOT TURGAN kataklari soni.
   *
   * Server bunday javonni o'chirtirmaydi (409). Reja esa ketma-ket va
   * tranzaksiyasiz yoziladi: rad javobi saqlash O'RTASIDA kelsa, undan
   * oldingi amallar allaqachon bajarilgan bo'ladi va odam yarim
   * saqlangan reja bilan qoladi. Shuning uchun savol o'chirish
   * BOSILGANDA beriladi, saqlashda emas.
   */
  occupiedCells: number;
}

export interface BuilderSnapshot {
  zones: DraftZone[];
  racks: DraftRack[];
}

const emptySnapshot = (): BuilderSnapshot => ({ zones: [], racks: [] });

const cloneSnapshot = (snapshot: BuilderSnapshot): BuilderSnapshot => ({
  zones: snapshot.zones.map((zone) => ({ ...zone, plan: { ...zone.plan } })),
  racks: snapshot.racks.map((rack) => ({
    ...rack,
    pos: { ...rack.pos },
    bays: rack.bays.map((row) => [...row]),
    levelHeights: [...rack.levelHeights],
  })),
});

export const isLocalId = (id: string): boolean => id.startsWith('local:');

/**
 * TANLOV — turi bilan birga.
 *
 * Faqat `id` YETMAYDI: zona va stellaj identifikatorlari alohida
 * ketma-ketliklardan keladi (`zone.id`, `rack.id`), ya'ni bazada
 * `zone 1` va `rack 1` bir vaqtda bo'ladi. Yalang'och satr bilan
 * solishtirilganda ikkisi bir-biriga o'xshab qolardi va oqibati og'ir edi:
 *
 *   - zonani bosgan operator xossalar panelida STELLAJNI ko'rardi, ya'ni
 *     zonani tahrirlash umuman imkonsiz bo'lardi;
 *   - zonani o'chirishga bergan buyruq o'sha raqamli STELLAJNI o'chirardi,
 *     kataklari bilan.
 */
export type BuilderSelection = { kind: 'zone' | 'rack'; id: string };

/** Tanlov aynan shu obyektga tegishlimi. */
export const isSelected = (
  selected: BuilderSelection | null,
  kind: BuilderSelection['kind'],
  id: string,
): boolean => selected !== null && selected.kind === kind && selected.id === id;

export interface BuilderState extends BuilderSnapshot {
  warehouseId: string | null;
  floor: WarehouseFloor;
  /** Server holati — `reset` va saqlash farqini hisoblash uchun. */
  baseline: BuilderSnapshot;
  dirty: boolean;
  past: BuilderSnapshot[];
  future: BuilderSnapshot[];
  selected: BuilderSelection | null;

  loadFromLayout: (layout: LayoutDto) => void;
  select: (selected: BuilderSelection | null) => void;

  addZone: (zone: Omit<DraftZone, 'id'>) => string | null;
  updateZone: (id: string, patch: Partial<Omit<DraftZone, 'id'>>) => void;
  /**
   * Zonani ko'chiradi va ICHIDAGI JAVONLARNI ham o'zi bilan olib ketadi.
   * Aks holda zonani surish uning hamma javonini bir zumda "zonadan
   * chiqib ketgan" holatga tushirardi.
   */
  moveZone: (id: string, x: number, y: number) => void;
  removeZone: (id: string) => void;

  addRack: (rack: Omit<DraftRack, 'id' | 'serverCells' | 'occupiedCells'>) => string | null;
  /**
   * Zonadagi javonlarni QAYTA TERADI — bir-biriga va zonaning chetiga
   * tegizib.
   *
   * Eski javonlar markazi panjaraga yaxlitlangan holda qo'yilgan va
   * ularning orasida 25 santimetrgacha keraksiz tirqish qolgan. Ularni
   * qo'lda surib tekislash amalda mumkin emas: bir javonni surganda
   * boshqasi bilan chetlari bir necha millimetrga to'g'ri kelmaydi.
   */
  compactZone: (zoneId: string) => void;
  updateRack: (id: string, patch: Partial<Omit<DraftRack, 'id' | 'serverCells' | 'occupiedCells'>>) => void;
  moveRack: (id: string, x: number, y: number) => void;
  removeRack: (id: string) => void;

  undo: () => void;
  redo: () => void;
  reset: () => void;
}

/** Tarixdagi qadam chegarasi — cheksiz o'sib xotirani yeb qo'ymasligi kerak. */
const HISTORY_LIMIT = 50;

const fromLayout = (layout: LayoutDto): BuilderSnapshot => ({
  zones: layout.zones.map((zone) => ({
    id: zone.id,
    code: zone.code,
    name: zone.name,
    color: zone.color,
    plan: { ...zone.plan },
  })),
  racks: layout.zones.flatMap((zone) =>
    zone.racks.map((rack) => ({
      id: rack.id,
      zoneId: zone.id,
      code: rack.code,
      name: rack.name,
      kind: rack.kind,
      pos: { ...rack.pos },
      depthM: rack.depthM,
      bays: rack.bays.map((row) => [...row]),
      levelHeights: [...rack.levelHeights],
      serverCells: rack.cells.length,
      occupiedCells: rack.cells.filter((cell) => cell.status === 'OCCUPIED').length,
    })),
  ),
});

let localCounter = 0;
const nextLocalId = (): string => {
  localCounter += 1;
  return `local:${localCounter}`;
};

export const useBuilderStore = create<BuilderState>()((set, get) => {
  /**
   * O'zgarishni tarix bilan birga yozadi. `mutate` `null` qaytarsa —
   * o'zgarish **qabul qilinmaydi** va holat butunlay tegilmaydi (chegaradan
   * chiqqan stellaj shu yo'l bilan rad etiladi).
   */
  const commit = (mutate: (draft: BuilderSnapshot) => BuilderSnapshot | null): boolean => {
    const state = get();
    const current: BuilderSnapshot = { zones: state.zones, racks: state.racks };
    const next = mutate(cloneSnapshot(current));
    if (next === null) return false;

    set({
      zones: next.zones,
      racks: next.racks,
      dirty: true,
      past: [...state.past.slice(-HISTORY_LIMIT + 1), cloneSnapshot(current)],
      future: [],
    });
    return true;
  };

  const rackById = (snapshot: BuilderSnapshot, id: string): DraftRack | undefined =>
    snapshot.racks.find((rack) => rack.id === id);

  return {
    ...emptySnapshot(),
    warehouseId: null,
    floor: { lengthM: 0, widthM: 0 },
    baseline: emptySnapshot(),
    dirty: false,
    past: [],
    future: [],
    selected: null,

    loadFromLayout: (layout) => {
      const snapshot = fromLayout(layout);
      set({
        warehouseId: layout.warehouse.id,
        floor: { lengthM: layout.warehouse.dims.lengthM, widthM: layout.warehouse.dims.widthM },
        baseline: cloneSnapshot(snapshot),
        ...cloneSnapshot(snapshot),
        dirty: false,
        past: [],
        future: [],
        selected: null,
      });
    },

    select: (selected) => set({ selected }),

    addZone: (zone) => {
      const plan = {
        x: snap(zone.plan.x),
        y: snap(zone.plan.y),
        w: snap(zone.plan.w),
        d: snap(zone.plan.d),
      };
      if (!rectWithinFloor(plan, get().floor)) return null;

      const id = nextLocalId();
      const accepted = commit((draft) => {
        if (draft.zones.some((existing) => existing.code === zone.code)) return null;
        draft.zones.push({ ...zone, id, plan });
        return draft;
      });
      return accepted ? id : null;
    },

    updateZone: (id, patch) => {
      commit((draft) => {
        const zone = draft.zones.find((candidate) => candidate.id === id);
        if (zone === undefined) return null;
        if (patch.plan !== undefined) {
          const plan = {
            x: snap(patch.plan.x),
            y: snap(patch.plan.y),
            w: snap(patch.plan.w),
            d: snap(patch.plan.d),
          };
          if (!rectWithinFloor(plan, get().floor)) return null;
          zone.plan = plan;
        }
        if (patch.code !== undefined) zone.code = patch.code;
        if (patch.name !== undefined) zone.name = patch.name;
        if (patch.color !== undefined) zone.color = patch.color;
        return draft;
      });
    },

    moveZone: (id, x, y) => {
      commit((draft) => {
        const zone = draft.zones.find((candidate) => candidate.id === id);
        if (zone === undefined) return null;
        const plan = { ...zone.plan, x: snap(x), y: snap(y) };
        if (!rectWithinFloor(plan, get().floor)) return null;

        const dx = plan.x - zone.plan.x;
        const dy = plan.y - zone.plan.y;
        // Javonlar bilan birga surilgani uchun ularning chegarasi ham
        // qaytadan tekshiriladi: zona devor ichida, javon esa tashqarida
        // qolib ketishi mumkin emas.
        const moved = draft.racks
          .filter((rack) => rack.zoneId === id)
          .map((rack) => ({ ...rack, pos: { ...rack.pos, x: rack.pos.x + dx, y: rack.pos.y + dy } }));
        if (moved.some((rack) => !withinFloor(rack, get().floor))) return null;

        zone.plan = plan;
        for (const rack of draft.racks) {
          if (rack.zoneId !== id) continue;
          rack.pos = { ...rack.pos, x: rack.pos.x + dx, y: rack.pos.y + dy };
        }
        return draft;
      });
    },

    removeZone: (id) => {
      commit((draft) => {
        if (draft.racks.some((rack) => rack.zoneId === id)) return null;
        const index = draft.zones.findIndex((zone) => zone.id === id);
        if (index === -1) return null;
        draft.zones.splice(index, 1);
        return draft;
      });
      if (isSelected(get().selected, 'zone', id)) set({ selected: null });
    },

    /*
     * Koordinata YAXLITLANMAYDI: joyni `placeInZone` topgan va u javonni
     * ataylab zonaning chetiga yoki qo'shnisiga TEGIZIB qo'ygan. Markazni
     * qayta yaxlitlash o'sha tekislashni buzib, orada 12 santimetrgacha
     * tirqish qoldirardi.
     */
    addRack: (rack) => {
      const placed = { ...rack, pos: { ...rack.pos } };
      if (!withinFloor(placed, get().floor)) return null;

      const id = nextLocalId();
      const accepted = commit((draft) => {
        const clash = draft.racks.some(
          (existing) => existing.zoneId === rack.zoneId && existing.code === rack.code,
        );
        if (clash) return null;
        /* Yangi javon — hali serverda yo'q, ya'ni ichida hech narsa yo'q. */
        draft.racks.push({ ...placed, id, serverCells: 0, occupiedCells: 0 });
        return draft;
      });
      return accepted ? id : null;
    },

    compactZone: (zoneId) => {
      commit((draft) => {
        const zone = draft.zones.find((candidate) => candidate.id === zoneId);
        if (zone === undefined) return null;

        /* Tartib JORIY JOYLASHUVdan olinadi, ro'yxatdagi tartibdan emas:
           odam javonni qayerga surgan bo'lsa, o'sha yerdagi navbatini
           saqlab qoladi — zichlash uni javonlar orasidan sug'urib
           olmasligi kerak. */
        const mine = draft.racks
          .filter((rack) => rack.zoneId === zoneId)
          .sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x);
        if (mine.length === 0) return null;

        const placed: RackPlacement[] = draft.racks.filter((rack) => rack.zoneId !== zoneId);
        for (const rack of mine) {
          const at = placeInZone(zone.plan, rack, placed, get().floor, rack.pos.rotationDeg);
          // Sig'masa O'Z JOYIDA qoladi: uni zonadan tashqariga otib
          // yuborishdan ko'ra shu yerda ko'rinib turgani yaxshi.
          if (at !== null) rack.pos = { ...rack.pos, ...at };
          placed.push(rack);
        }
        return draft;
      });
    },

    updateRack: (id, patch) => {
      commit((draft) => {
        const rack = rackById(draft, id);
        if (rack === undefined) return null;
        const candidate: DraftRack = {
          ...rack,
          ...patch,
          pos: patch.pos === undefined ? rack.pos : { ...patch.pos },
          bays: patch.bays === undefined ? rack.bays : patch.bays.map((row) => [...row]),
          levelHeights:
            patch.levelHeights === undefined ? rack.levelHeights : [...patch.levelHeights],
        };
        // Bu yerda ham yaxlitlash yo'q — sabab `addRack` dagi bilan bir xil.
        if (!withinFloor(candidate, get().floor)) return null;
        Object.assign(rack, candidate);
        return draft;
      });
    },

    /**
     * Javonning CHETI 0.25 m panjaraga yopishadi; devordan chiqsa rad etiladi.
     *
     * Markazni yaxlitlash javonni panjaradan chiqarib yuborardi — qarang
     * `snapRackPos`.
     */
    moveRack: (id, x, y) => {
      commit((draft) => {
        const rack = rackById(draft, id);
        if (rack === undefined) return null;
        const pos = {
          ...rack.pos,
          ...snapRackPos({ pos: { ...rack.pos, x, y }, bays: rack.bays, depthM: rack.depthM }),
        };
        if (!withinFloor({ pos, bays: rack.bays, depthM: rack.depthM }, get().floor)) return null;
        rack.pos = pos;
        return draft;
      });
    },


    removeRack: (id) => {
      commit((draft) => {
        const index = draft.racks.findIndex((rack) => rack.id === id);
        if (index === -1) return null;
        draft.racks.splice(index, 1);
        return draft;
      });
      if (isSelected(get().selected, 'rack', id)) set({ selected: null });
    },

    undo: () => {
      const state = get();
      const previous = state.past[state.past.length - 1];
      if (previous === undefined) return;
      set({
        ...cloneSnapshot(previous),
        past: state.past.slice(0, -1),
        future: [cloneSnapshot({ zones: state.zones, racks: state.racks }), ...state.future],
        dirty: true,
      });
    },

    redo: () => {
      const state = get();
      const next = state.future[0];
      if (next === undefined) return;
      set({
        ...cloneSnapshot(next),
        past: [...state.past, cloneSnapshot({ zones: state.zones, racks: state.racks })],
        future: state.future.slice(1),
        dirty: true,
      });
    },

    /** Qoralamani butunlay tashlab, server holatiga qaytadi. */
    reset: () => {
      const state = get();
      set({
        ...cloneSnapshot(state.baseline),
        dirty: false,
        past: [],
        future: [],
        selected: null,
      });
    },
  };
});

/** Stellajdagi katak soni — kontraktdagi funksiya, qayta hisoblanmaydi. */
export const draftCellCount = (rack: Pick<DraftRack, 'bays' | 'levelHeights'>): number =>
  rackCellCount({ ...rack, depthM: 0 });

export interface PlanDiff {
  zonesToCreate: DraftZone[];
  zonesToUpdate: DraftZone[];
  zonesToDelete: string[];
  racksToCreate: DraftRack[];
  racksToUpdate: DraftRack[];
  racksToDelete: string[];
  /** Saqlashdan oldingi oldindan ko'rish uchun. */
  cellsToCreate: number;
  cellsToRemove: number;
}

const sameZone = (a: DraftZone, b: DraftZone): boolean =>
  a.code === b.code &&
  a.name === b.name &&
  a.color === b.color &&
  a.plan.x === b.plan.x &&
  a.plan.y === b.plan.y &&
  a.plan.w === b.plan.w &&
  a.plan.d === b.plan.d;

/** Ro'yxatlar element-ba-element solishtiriladi — uzunligi ham, qiymati ham. */
const sameList = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/** Qatorlar bo'yicha bo'linish — qator soni ham, ichidagi o'rinlar ham. */
const sameRows = (a: readonly number[][], b: readonly number[][]): boolean =>
  a.length === b.length && a.every((row, index) => sameList(row, b[index] ?? []));

const sameRack = (a: DraftRack, b: DraftRack): boolean =>
  a.code === b.code &&
  a.name === b.name &&
  a.pos.x === b.pos.x &&
  a.pos.y === b.pos.y &&
  a.pos.rotationDeg === b.pos.rotationDeg &&
  a.depthM === b.depthM &&
  sameRows(a.bays, b.bays) &&
  sameList(a.levelHeights, b.levelHeights);

/**
 * Qoralama bilan server holati orasidagi farq.
 *
 * Saqlashdan oldin odamga aynan shu ko'rsatiladi ("3 zona, 12 stellaj,
 * 480 katak yaratiladi"), shuning uchun sanoq bu yerda hisoblanadi — ikkinchi
 * marta UI da emas.
 */
export const planDiff = (baseline: BuilderSnapshot, draft: BuilderSnapshot): PlanDiff => {
  const baseZones = new Map(baseline.zones.map((zone) => [zone.id, zone]));
  const baseRacks = new Map(baseline.racks.map((rack) => [rack.id, rack]));

  const zonesToCreate = draft.zones.filter((zone) => isLocalId(zone.id));
  const zonesToUpdate = draft.zones.filter((zone) => {
    const before = baseZones.get(zone.id);
    return before !== undefined && !sameZone(before, zone);
  });
  const draftZoneIds = new Set(draft.zones.map((zone) => zone.id));
  const zonesToDelete = baseline.zones
    .filter((zone) => !draftZoneIds.has(zone.id))
    .map((zone) => zone.id);

  const racksToCreate = draft.racks.filter((rack) => isLocalId(rack.id));
  const racksToUpdate = draft.racks.filter((rack) => {
    const before = baseRacks.get(rack.id);
    return before !== undefined && !sameRack(before, rack);
  });
  const draftRackIds = new Set(draft.racks.map((rack) => rack.id));
  const racksToDelete = baseline.racks
    .filter((rack) => !draftRackIds.has(rack.id))
    .map((rack) => rack.id);

  let cellsToCreate = racksToCreate.reduce((sum, rack) => sum + draftCellCount(rack), 0);
  let cellsToRemove = baseline.racks
    .filter((rack) => !draftRackIds.has(rack.id))
    .reduce((sum, rack) => sum + rack.serverCells, 0);

  for (const rack of racksToUpdate) {
    const before = baseRacks.get(rack.id);
    if (before === undefined) continue;
    const delta = draftCellCount(rack) - draftCellCount(before);
    if (delta > 0) cellsToCreate += delta;
    else cellsToRemove += -delta;
  }

  return {
    zonesToCreate,
    zonesToUpdate,
    zonesToDelete,
    racksToCreate,
    racksToUpdate,
    racksToDelete,
    cellsToCreate,
    cellsToRemove,
  };
};
